/**
 * Create-agent → flow page handoff: the agent already collected the user's answers
 * (`quickIntake.creationPlanId` + `prefill`), so flow pages only ask for what their own
 * start function still needs and then start generation.
 */

const STARTED_KEY = 'twinnee.agentHandoff.started';

/** Agent handoff info from router state, or null when the page was opened some other way. */
export function readAgentHandoff(locationState) {
  const quick = locationState?.quickIntake || {};
  const planId = String(quick.creationPlanId || '').trim();
  if (!planId) return null;
  return {
    planId,
    flowId: quick.flowId || '',
    autoStart: Boolean(locationState?.agentAutoStart),
    input: quick.creationInput && typeof quick.creationInput === 'object' ? quick.creationInput : {},
  };
}

function readStarted() {
  try {
    const raw = sessionStorage.getItem(STARTED_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

/** True once this plan's generation was started from a flow page in this browser session. */
export function handoffStarted(planId) {
  return Boolean(planId) && readStarted().includes(planId);
}

export function markHandoffStarted(planId) {
  if (!planId) return;
  try {
    const ids = readStarted().filter((id) => id !== planId);
    ids.push(planId);
    sessionStorage.setItem(STARTED_KEY, JSON.stringify(ids.slice(-50)));
  } catch {
    /* storage unavailable — worst case the gate shows again */
  }
}

/** Fields (from a page's required list) that still need an answer. */
export function missingHandoffFields(fields) {
  return (fields || []).filter((f) => f && !f.valid);
}

/** Whole-number minutes for a duration in seconds (at least 1), or `fallback` when unknown. */
export function durationMinutes(seconds, fallback) {
  const n = Number(seconds);
  return Number.isFinite(n) && n > 0 ? Math.max(1, Math.round(n / 60)) : fallback;
}

/** `value` when it is one of the select `options` (`[{ value }]` or plain values), else `fallback`. */
export function pickOption(value, options, fallback) {
  const v = value == null ? '' : String(value);
  if (!v) return fallback;
  const hit = (options || []).some((o) => String(o && typeof o === 'object' ? o.value : o) === v);
  return hit ? v : fallback;
}

/** Closest number in `values` to `n`, or `fallback` when `n` isn't a positive number. */
export function nearestValue(n, values, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0 || !values?.length) return fallback;
  return values.reduce((best, v) => (Math.abs(v - x) < Math.abs(best - x) ? v : best), values[0]);
}
